import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Star, X } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { cn } from "@/lib/utils/cn";
import { pillButtonClass } from "@/pages/member/ui/styles";
import {
  memberPendingReviews,
  memberSubmitReview,
  memberSubmitClassReview,
  type PendingReviewItem,
  type ReviewQuestionOption,
  type ReviewAnswer,
} from "@/lib/api/member";

// ---------------------------------------------------------------------------
// Snooze — sessionStorage so it clears on hard refresh / new login
// ---------------------------------------------------------------------------

const SNOOZE_KEY = "gymviz.review_snooze";

function getSnoozed(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SNOOZE_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
}

function snoozeItem(bookingId: string) {
  const set = getSnoozed();
  set.add(bookingId);
  sessionStorage.setItem(SNOOZE_KEY, JSON.stringify([...set]));
}

function isSnoozed(bookingId: string): boolean {
  return getSnoozed().has(bookingId);
}

// ---------------------------------------------------------------------------
// Single star row
// ---------------------------------------------------------------------------

function StarRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
      <span className="text-sm text-ink-700 sm:flex-1">{label}</span>
      <div className="flex gap-1" role="group" aria-label={label} onMouseLeave={() => setHovered(0)}>
        {[1, 2, 3, 4, 5].map((s) => {
          const on = (hovered || value) >= s;
          return (
            <button
              key={s}
              type="button"
              onClick={() => onChange(s)}
              onMouseEnter={() => setHovered(s)}
              aria-label={t("memberUi.review.stars", { count: s, defaultValue: "{{count}} stars" })}
              aria-pressed={value === s}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 sm:h-9 sm:w-9"
            >
              <Star
                className={cn("h-7 w-7 transition-colors sm:h-6 sm:w-6", on ? "text-warning-500" : "text-ink-300")}
                fill={on ? "currentColor" : "none"}
                strokeWidth={1.5}
                aria-hidden="true"
              />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main prompt
// ---------------------------------------------------------------------------

export function PendingReviewsPrompt() {
  const { t, i18n } = useTranslation();
  const reduce = useReducedMotion();
  const qc = useQueryClient();

  const { data } = useQuery({
    queryKey: ["member", "pending-reviews"],
    queryFn: memberPendingReviews,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const [queue, setQueue] = useState<PendingReviewItem[]>([]);
  const [questions, setQuestions] = useState<ReviewQuestionOption[]>([]);

  useEffect(() => {
    if (!data?.items) return;
    setQueue(data.items.filter((item) => !isSnoozed(item.booking_id)));
    setQuestions(data.questions ?? []);
  }, [data]);

  const current = queue[0] ?? null;

  // Per-question answers: map of question_id → stars
  const [answers, setAnswers] = useState<Record<string, number>>({});
  // Fallback single overall star (used when no questions)
  const [overallStars, setOverallStars] = useState(0);
  const [error, setError] = useState("");

  // Reset when item changes
  useEffect(() => {
    setAnswers({});
    setOverallStars(0);
    setError("");
  }, [current?.booking_id]);

  // Filter questions by session type
  const visibleQuestions = questions.filter(
    (q) => q.applies_to === "both" || q.applies_to === current?.type,
  );

  const hasQuestions = visibleQuestions.length > 0;

  // Ready to submit: all visible questions answered, or fallback star set
  const canSubmit = hasQuestions
    ? visibleQuestions.every((q) => (answers[q.id] ?? 0) > 0)
    : overallStars > 0;

  const submitFn = current?.type === "pt" ? memberSubmitReview : memberSubmitClassReview;

  const mutation = useMutation({
    mutationFn: () => {
      const payload = hasQuestions
        ? {
            answers: visibleQuestions.map((q): ReviewAnswer => ({
              question_id: q.id,
              stars: answers[q.id],
            })),
          }
        : { stars: overallStars };
      return submitFn(current!.booking_id, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["member", "pending-reviews"] });
      qc.invalidateQueries({ queryKey: ["member", "pt-bookings"] });
      setQueue((q) => q.slice(1));
    },
    onError: (err: unknown) => {
      const e = err as { message?: string };
      setError(e.message || t("common.errorLoading"));
    },
  });

  function handleSkip() {
    if (!current) return;
    snoozeItem(current.booking_id);
    setQueue((q) => q.slice(1));
  }

  if (!current) return null;

  const dateLabel = current.date
    ? new Date(current.date).toLocaleDateString(i18n.language, {
        weekday: "short",
        month: "short",
        day: "numeric",
      })
    : "";

  return (
    <motion.section
      role="dialog"
      aria-modal="false"
      aria-labelledby="review-prompt-title"
      initial={reduce ? false : { opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="fixed inset-x-3 bottom-[calc(5.75rem+env(safe-area-inset-bottom))] z-40 max-h-[70vh] overflow-y-auto rounded-[24px] border border-border-100 bg-surface p-5 shadow-[0_12px_48px_rgba(15,46,38,0.22)] sm:inset-x-auto sm:end-4 sm:w-[380px] lg:bottom-6 lg:end-6"
    >
      <button
        type="button"
        onClick={handleSkip}
        disabled={mutation.isPending}
        aria-label={t("memberPortal.review.remindLater", "Remind me later")}
        className="absolute end-3 top-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-ink-500 hover:bg-ink-900/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>

      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-primary-600 dark:text-primary-400">
        {current.type === "pt"
          ? t("memberPortal.review.typeLabel.pt", "Personal Training")
          : t("memberPortal.review.typeLabel.class", "Group Class")}
      </p>
      <h2 id="review-prompt-title" className="mt-1 pe-10 text-lg font-semibold tracking-tight text-ink-900">
        {t("memberPortal.review.promptTitle", "How was your session?")}
      </h2>
      <p className="mt-0.5 text-sm text-ink-500">
        {[
          current.session_name
            ? `${current.session_name} — ${current.trainer_name ?? "—"}`
            : (current.trainer_name ?? "—"),
          dateLabel,
        ].filter(Boolean).join(" · ")}
      </p>

      <div className="mt-4 space-y-3">
        {hasQuestions ? (
          visibleQuestions.map((q) => (
            <StarRow
              key={q.id}
              label={q.text_en}
              value={answers[q.id] ?? 0}
              onChange={(n) => setAnswers((prev) => ({ ...prev, [q.id]: n }))}
            />
          ))
        ) : (
          <StarRow
            label={t("memberPortal.review.rateTrainer", "Rate your trainer")}
            value={overallStars}
            onChange={setOverallStars}
          />
        )}
      </div>

      {queue.length > 1 && (
        <p className="mt-3 text-xs text-ink-400">
          {t("memberUi.review.more", { count: queue.length - 1, defaultValue: "{{count}} more to review after this" })}
        </p>
      )}

      {error && <p role="alert" className="mt-3 text-sm text-danger-500">{error}</p>}

      <div className="mt-4 flex gap-2">
        <button type="button" className={cn(pillButtonClass("ghost", "md"), "flex-1")} onClick={handleSkip} disabled={mutation.isPending}>
          {t("memberPortal.review.remindLater", "Remind me later")}
        </button>
        <button
          type="button"
          className={cn(pillButtonClass("primary", "md"), "flex-1")}
          onClick={() => mutation.mutate()}
          disabled={!canSubmit || mutation.isPending}
        >
          {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          {mutation.isPending ? t("wizards.common.loading") : t("memberPortal.pt.reviewSubmit", "Submit Review")}
        </button>
      </div>
    </motion.section>
  );
}
